import { formatUrl, getCurrentUrl } from '@/state/utils';

import { Cache } from '@/utils/storage';
import { parseQueryString } from '@/utils/strings';

// Custom storage type
export type CustomStateStorage = {
  getItem: (name: string) => string | null;
  setItem: (name: string, value: string) => void;
  removeItem: (name: string) => void;
};
// Custom promise storage type
export type CustomPromiseStateStorage = {
  getItem: (name: string) => Promise<string | null>;
  setItem: (name: string, value: string) => Promise<void>;
  removeItem: (name: string) => Promise<void>;
};

// Utility to save state to localStorage
export const customLocalStorage = <T>(initState: T): CustomStateStorage => ({
  getItem: (key: string): string => {
    const value = localStorage.getItem(key);
    if (value === null) return JSON.stringify({ state: { state: initState } });
    return value;
  },
  setItem: (key: string, value: string): void => {
    localStorage.setItem(key, value);
  },
  removeItem: localStorage.removeItem,
});

// Utility to save state to sessionStorage
export const customSessionStorage = <T>(initState: T): CustomStateStorage => ({
  getItem: (key: string): string => {
    const value = sessionStorage.getItem(key);
    if (value === null) return JSON.stringify({ state: { state: initState } });
    return value;
  },
  setItem: (key: string, value: string): void => {
    sessionStorage.setItem(key, value);
  },
  removeItem: sessionStorage.removeItem,
});

// Utility to save state to the query string
export const customSearchStorage = (initState): CustomStateStorage => {
  const getSearchStorage = (initState) => {
    const parsed = { ...initState, ...parseQueryString(window.location.search) };
    const validKeys = Object.keys(initState);
    const state = Object.entries(parsed).reduce((acc, [key, value]: [string, string]) => {
      if (!validKeys.includes(key)) return acc;
      acc[key] = value === 'undefined' ? '' : value?.includes(',') ? value.split(',') : value;
      return acc;
    }, {});
    return JSON.stringify({ state });
  };
  const saveSearchStorage = (state) => {
    // get current query string
    const current = getCurrentUrl();
    // convert state to search
    const parsed = Object.entries(state).reduce(
      (acc, [key, value]: [string, unknown]) =>
        value === undefined ? { ...acc, [key]: '' } : { ...acc, [key]: Array.isArray(value) ? value.join(',') : value },
      {}
    );
    const url = formatUrl(undefined, parsed);
    // check if the url changed
    if (current === url) return;
    // otherwise add to history new state
    window.history.pushState({}, '', url);
  };
  return {
    getItem: (_key: string): string => getSearchStorage(initState),
    setItem: (_key: string, value: string): void => {
      const {
        state: { state },
      } = JSON.parse(value);
      saveSearchStorage(state);
    },
    removeItem: (_key: string): void => {
      window.history.replaceState({}, '', `${window.location.pathname}`);
    },
  };
};

// Utility to save state to indexedDB
export const customIndexedDBStorage = <T>(initState: T, db = Cache): CustomPromiseStateStorage => ({
  getItem: async (name: string) => {
    let data = await db.get(name, initState);
    if (!data) {
      data = initState;
      db.set(name, data);
    }
    return JSON.stringify({ state: { state: data } });
  },
  setItem: async (name: string, value: string) => {
    const {
      state: { state },
    } = JSON.parse(value);
    await db.set(name, state);
  },
  removeItem: async (name: string) => {
    await db.delete(name);
  },
});
